import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const DevisContext = createContext(null);
export const useDevis = () => useContext(DevisContext);

const TYPES = ["Pneus", "Parallélisme", "Climatisation", "Mécanique", "Autre"];

export function FormulaireDevis({ prefill = "", compact = false, onSucces }) {
  const [form, setForm] = useState({
    nom: "", telephone: "", email: "", type_demande: "Pneus",
    message: prefill, consentement: false, entreprise: "",
  });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState("");
  const [confirme, setConfirme] = useState(false);

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, message: prefill }));
  }, [prefill]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const envoyer = async (e) => {
    e.preventDefault();
    setErreur("");
    if (!form.nom.trim() || !form.telephone.trim()) {
      setErreur("Merci d'indiquer votre nom et votre numéro de téléphone.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setErreur("Merci d'indiquer une adresse email valide.");
      return;
    }
    if (!form.consentement) {
      setErreur("Merci de cocher la case de consentement pour envoyer votre demande.");
      return;
    }
    setEnvoi(true);
    try {
      await api.post("/demandes", form);
      setConfirme(true);
      onSucces && onSucces();
    } catch (err) {
      setErreur(formatApiError(err.response?.data?.detail));
    } finally {
      setEnvoi(false);
    }
  };

  if (confirme) {
    return (
      <div data-testid="devis-confirmation" className="text-center py-10 px-4">
        <CheckCircle2 size={56} className="text-brand-red mx-auto mb-4" aria-hidden="true" />
        <p className="font-heading font-semibold uppercase text-2xl text-brand-dark leading-snug">
          Votre demande est bien reçue.
        </p>
        <p className="font-heading font-semibold uppercase text-2xl text-brand-red leading-snug mt-1">
          Nous vous rappelons sous 24 h ouvrées.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="formulaire-devis" onSubmit={envoyer} className="space-y-4" noValidate>
      <div className={compact ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label htmlFor="devis-nom" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">Nom</label>
          <input id="devis-nom" data-testid="devis-nom" type="text" value={form.nom} onChange={set("nom")}
            placeholder="Votre nom" autoComplete="name"
            className="w-full bg-brand-light border-b-2 border-gray-300 focus:border-brand-red px-4 py-3.5 outline-none font-body text-brand-dark transition-colors" />
        </div>
        <div>
          <label htmlFor="devis-telephone" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">Téléphone</label>
          <input id="devis-telephone" data-testid="devis-telephone" type="tel" value={form.telephone} onChange={set("telephone")}
            placeholder="06 12 34 56 78" autoComplete="tel"
            className="w-full bg-brand-light border-b-2 border-gray-300 focus:border-brand-red px-4 py-3.5 outline-none font-body text-brand-dark transition-colors" />
        </div>
      </div>
      <div className={compact ? "space-y-4" : "grid grid-cols-1 sm:grid-cols-2 gap-4"}>
        <div>
          <label htmlFor="devis-email" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">Email</label>
          <input id="devis-email" data-testid="devis-email" type="email" value={form.email} onChange={set("email")}
            placeholder="vous@exemple.fr" autoComplete="email"
            className="w-full bg-brand-light border-b-2 border-gray-300 focus:border-brand-red px-4 py-3.5 outline-none font-body text-brand-dark transition-colors" />
        </div>
        <div>
          <label htmlFor="devis-type" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">Type de demande</label>
          <select id="devis-type" data-testid="devis-type" value={form.type_demande} onChange={set("type_demande")}
            className="w-full bg-brand-light border-b-2 border-gray-300 focus:border-brand-red px-4 py-3.5 outline-none font-body text-brand-dark transition-colors">
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="devis-message" className="block text-sm font-heading uppercase tracking-wider text-brand-dark mb-2">Message</label>
        <textarea id="devis-message" data-testid="devis-message" rows={4} value={form.message} onChange={set("message")}
          placeholder="Décrivez votre besoin (dimension des pneus, panne constatée…)"
          className="w-full bg-brand-light border-b-2 border-gray-300 focus:border-brand-red px-4 py-3.5 outline-none font-body text-brand-dark transition-colors resize-y" />
      </div>
      <div className="hidden" aria-hidden="true">
        <label htmlFor="devis-entreprise">Entreprise</label>
        <input id="devis-entreprise" type="text" tabIndex={-1} autoComplete="off"
          value={form.entreprise} onChange={set("entreprise")} />
      </div>
      <div className="flex items-start gap-3">
        <input id="devis-consentement" data-testid="devis-consentement" type="checkbox"
          checked={form.consentement} onChange={set("consentement")}
          className="mt-1 w-5 h-5 accent-[#E8492F] shrink-0" />
        <label htmlFor="devis-consentement" className="text-sm text-brand-body leading-relaxed">
          J'accepte que mes informations soient utilisées uniquement pour traiter ma demande,
          conformément à la politique de confidentialité.
        </label>
      </div>
      {erreur && (
        <p data-testid="devis-erreur" role="alert" className="text-brand-redhover font-body font-medium bg-red-50 border-l-4 border-brand-red px-4 py-3">
          {erreur}
        </p>
      )}
      <button type="submit" data-testid="devis-envoyer" disabled={envoi}
        className="w-full bg-brand-red hover:bg-brand-redhover disabled:opacity-60 text-white font-heading font-semibold uppercase tracking-wider text-xl py-4 transition-colors">
        {envoi ? "Envoi en cours…" : "Envoyer ma demande"}
      </button>
    </form>
  );
}

export function DevisProvider({ children }) {
  const [ouvert, setOuvert] = useState(false);
  const [prefill, setPrefill] = useState("");
  const [cle, setCle] = useState(0);

  const openDevis = useCallback((message = "") => {
    setPrefill(message);
    setCle((c) => c + 1);
    setOuvert(true);
  }, []);

  return (
    <DevisContext.Provider value={{ openDevis }}>
      {children}
      <AnimatePresence>
        {ouvert && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setOuvert(false)}
            role="dialog" aria-modal="true" aria-label="Demande de devis"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-6 sm:p-8 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="modale-devis"
            >
              <button
                data-testid="modale-devis-fermer"
                onClick={() => setOuvert(false)}
                aria-label="Fermer la fenêtre"
                className="absolute top-3 right-3 p-3 text-brand-dark hover:text-brand-red transition-colors"
              >
                <X size={26} />
              </button>
              <h2 className="font-heading font-bold uppercase text-3xl text-brand-dark mb-1">
                Demander un devis
              </h2>
              <p className="text-brand-body mb-6">Réponse par téléphone ou email sous 24 h ouvrées. Gratuit, sans engagement.</p>
              <FormulaireDevis key={cle} prefill={prefill} compact onSucces={() => {}} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DevisContext.Provider>
  );
}
